import { useTheme, Box, Typography, CircularProgress } from '@mui/material';
import { forwardRef, useState, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { CloudUpIcon, DeleteIcon, DocumentationIcon } from '../../icons/index.js';
import { DropzoneContainer, DropzoneDragArea, DropzoneFileContainer, OptionsFileButton, DropzonePlaceholderContainer, DropzoneLoadingContainer } from './styled.js';
import { SingleFileDropzone } from './single-file-dropzone.js';
import { jsxs, jsx } from 'react/jsx-runtime';

const isImage = file => /^image/.exec(file.type) ?? (file.type === '' && file.name.includes('.jpe'));
const Dropzone = /*#__PURE__*/forwardRef(function Dropzone(props, ref) {
  const {
    helperText,
    onDropFile,
    onFilesRejected,
    isLoading = false,
    // percentageProgress,
    error = false,
    preloadFiles = [],
    optionsMenu,
    optionsMenuLabels,
    dataTestid = 'dropzone',
    disabled = false,
    accept,
    maxSize,
    maxFiles = 0,
    multiple = true,
    hideFileName = false,
    downloadUrl,
    backgroundColor,
    sx
  } = props;
  const [files, setFiles] = useState(preloadFiles);
  const adjustedMaxSize = useMemo(() => {
    if (!maxSize) {
      return undefined;
    }
    const usedBytes = files.reduce((combinedSize, {
      file
    }) => combinedSize + (file?.size ?? 0), 0);
    return maxSize - usedBytes;
  }, [maxSize, files]);
  const dropzoneOptions = {
    accept,
    maxSize: adjustedMaxSize,
    maxFiles,
    multiple,
    disabled
  };
  const label = props.label ?? 'Add File';
  const theme = useTheme();
  const isUploadShown = useMemo(() => {
    if (multiple === false) {
      return false;
    }
    if (maxFiles > 0 && maxFiles <= files.length) {
      return false;
    }
    return true;
  }, [multiple, maxFiles, files.length]);
  const onDrop = (acceptedFiles, rejectedFiles) => {
    const items = acceptedFiles.map(file => ({
      fileUrl: isImage(file) ? URL.createObjectURL(file) : undefined,
      file
    }));
    const mergedFiles = multiple ? [...files, ...items] : items;
    setFiles(mergedFiles);
    props.setFiles?.(mergedFiles);
    if (onDropFile) {
      void onDropFile(mergedFiles);
    }
    if (onFilesRejected && rejectedFiles.length > 0) {
      onFilesRejected(rejectedFiles.map(({
        errors
      }) => ({
        errors: errors.map(({
          code
        }) => ({
          code
        }))
      })));
    }
  };
  const removeFile = file => event => {
    const newFiles = files.filter(f => f !== file);
    setFiles(newFiles);
    props.setFiles?.(newFiles);
    event.stopPropagation();
  };
  const onPreviewClick = (event, activeIndex) => {
    const images = files.filter(({
      fileUrl
    }) => fileUrl).map(({
      fileUrl
    }) => fileUrl);
    props.onPreviewClick?.(images, activeIndex);
    event.stopPropagation();
  };
  const {
    getRootProps,
    getInputProps,
    isDragReject,
    isDragAccept,
    open
  } = useDropzone({
    ...dropzoneOptions,
    onDrop
  });
  const borderColor = disabled ? theme.palette.gray40 : isDragReject ? theme.palette.error.main : isDragAccept ? theme.palette.success.main : theme.palette.gray50;
  const shouldCollapseDropzone = multiple === false && files[0]?.fileUrl;
  return /*#__PURE__*/jsxs(Box, {
    sx: sx,
    children: [/*#__PURE__*/jsxs(DropzoneContainer, {
      ...getRootProps(),
      "data-testid": `${dataTestid}-container`,
      onClick: open,
      className: "DropzoneContainer",
      sx: {
        width: shouldCollapseDropzone ? 'max-content' : '100%'
      },
      children: [/*#__PURE__*/jsx("input", {
        ref: ref,
        ...getInputProps(),
        "data-testid": `${dataTestid}-input`
      }), multiple === false && /*#__PURE__*/jsx(SingleFileDropzone, {
        label: label,
        file: files[0],
        onRemoveFile: removeFile(files[0]),
        downloadUrl: downloadUrl,
        onReplace: getRootProps().onClick,
        optionsMenu: optionsMenu,
        optionMenuLabels: optionsMenuLabels,
        backgroundColor: backgroundColor,
        sx: {
          borderColor,
          color: disabled ? theme.palette.gray50 : undefined,
          cursor: disabled ? 'default' : undefined
        }
      }), isUploadShown && /*#__PURE__*/jsxs(DropzoneDragArea, {
        className: ['DropzoneDragArea', files.length ? '' : 'DropzoneDragArea--empty'].join(' '),
        sx: {
          gridColumn: files.length ? 'auto' : '1 / 6',
          borderColor,
          color: disabled ? theme.palette.gray50 : undefined,
          cursor: disabled ? 'default' : undefined
        },
        children: [/*#__PURE__*/jsx(CloudUpIcon, {}), /*#__PURE__*/jsx(Typography, {
          variant: "body02",
          children: label
        })]
      }), multiple === true && files.map((file, index) => /*#__PURE__*/jsxs(DropzoneFileContainer, {
        children: [!disabled && /*#__PURE__*/jsx(OptionsFileButton, {
          onClick: removeFile(file),
          children: /*#__PURE__*/jsx(DeleteIcon, {})
        }), file.fileUrl ? /*#__PURE__*/jsx(Box, {
          component: "img",
          onClick: event => onPreviewClick(event, index),
          sx: {
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          },
          src: file.fileUrl
        }) : /*#__PURE__*/jsx(DropzonePlaceholderContainer, {
          children: /*#__PURE__*/jsx(DocumentationIcon, {
            sx: {
              fontSize: theme => theme.spacing(6)
            }
          })
        }), !hideFileName && /*#__PURE__*/jsx(Typography, {
          variant: "caption",
          "data-testid": `${dataTestid}-filename`,
          sx: {
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'center'
          },
          children: file.file?.name
        })]
      }, index)), isLoading && /*#__PURE__*/jsx(DropzoneLoadingContainer, {
        children: /*#__PURE__*/jsx(CircularProgress, {})
      })]
    }), helperText && /*#__PURE__*/jsx(Typography, {
      variant: "caption",
      "data-testid": `${dataTestid}-helper-text`,
      sx: {
        mt: 1,
        display: 'block',
        color: theme => error ? theme.palette.error.main : theme.palette.gray70
      },
      children: helperText
    })]
  });
});

export { Dropzone };
